import { Configuration } from "../configuration";
import { HttpClient } from "../http/HttpClient";
import { PaginatedResponse } from "../types/common";
import {
    ILeaderboardEntry,
    IQueryLeaderboardRequest,
    IDailyLeaderboardEntry,
    IOverwriteScoreRequest,
    IOverwriteScoreResponse,
    ISubmitScoreRequest,
    ISubmitScoreResponse,
} from "../types/leaderboard";

export class Leaderboard {
    private httpClient: HttpClient;

    constructor(configuration: Configuration) {
        this.httpClient = new HttpClient(configuration);
    }

    public async getCurrentEntries(gameId: string, filters?: IQueryLeaderboardRequest): Promise<PaginatedResponse<IDailyLeaderboardEntry> & {
        userEntry?: IDailyLeaderboardEntry | null;
        userRank?: number | null;
    }> {
        return await this.httpClient.get<PaginatedResponse<IDailyLeaderboardEntry> & {
            userEntry?: IDailyLeaderboardEntry | null;
            userRank?: number | null;
        }>(`/leaderboards/${gameId}/entries/current`, filters);
    }

    public async getTotalEntries(gameId: string, filters?: IQueryLeaderboardRequest): Promise<PaginatedResponse<ILeaderboardEntry> & {
        userEntry?: ILeaderboardEntry | null;
        userRank?: number | null;
    }> {
        return await this.httpClient.get<PaginatedResponse<ILeaderboardEntry> & {
            userEntry?: ILeaderboardEntry | null;
            userRank?: number | null;
        }>(`/leaderboards/${gameId}/entries/total`, filters);
    }

    public async getDailyEntries(gameId: string, date: string, filters?: IQueryLeaderboardRequest): Promise<PaginatedResponse<IDailyLeaderboardEntry> & {
        userEntry?: IDailyLeaderboardEntry | null;
        userRank?: number | null;
    }> {
        return await this.httpClient.get<PaginatedResponse<IDailyLeaderboardEntry> & {
            userEntry?: IDailyLeaderboardEntry | null;
            userRank?: number | null;
        }>(`/leaderboards/${gameId}/entries/daily/${date}`, filters);
    }

    public async getUserEntry(gameId: string, userId: string, guestId?: string): Promise<ILeaderboardEntry> {
        return await this.httpClient.get<ILeaderboardEntry>(
            `/leaderboards/${gameId}/entries/${encodeURIComponent(userId)}`,
            undefined,
            guestId ? { headers: { 'x-anonymous-id': guestId } } : undefined,
        );
    }

    public async getUserDailyHistory(gameId: string, userId: string, filters?: {
        startDate?: string;
        endDate?: string;
        page?: number;
        limit?: number;
    }): Promise<PaginatedResponse<IDailyLeaderboardEntry>> {
        return await this.httpClient.get<PaginatedResponse<IDailyLeaderboardEntry>>(`/leaderboards/${gameId}/entries/${encodeURIComponent(userId)}/history`, filters);
    }

    public async getLeaderboardEntries(gameId: string, filters?: IQueryLeaderboardRequest): Promise<PaginatedResponse<IDailyLeaderboardEntry> & {
        userEntry?: IDailyLeaderboardEntry | null;
        userRank?: number | null;
    }> {
        return await this.httpClient.get<PaginatedResponse<IDailyLeaderboardEntry> & {
            userEntry?: IDailyLeaderboardEntry | null;
            userRank?: number | null;
        }>(`/leaderboards/${gameId}/entries`, filters);
    }

    public async submitScore(gameId: string, body: ISubmitScoreRequest): Promise<ISubmitScoreResponse> {
        return await this.httpClient.post<ISubmitScoreResponse>(this.scoreEndpoint(gameId), body);
    }

    public async overwriteScore(gameId: string, body: IOverwriteScoreRequest): Promise<IOverwriteScoreResponse> {
        return await this.httpClient.put<IOverwriteScoreResponse>(this.scoreEndpoint(gameId), body);
    }

    private scoreEndpoint(gameId: string): string {
        return `/games/${gameId}/score`;
    }
}
